import { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { coreAPI, AsyncStatusResponse } from '@/utils/api/lawyerAPI';
import { STATUS_POLL_INTERVAL } from '@/config/timeouts';

interface AsyncStatusIndicatorProps {
  /** Whether the sidebar is collapsed */
  isCollapsed: boolean;
}

/**
 * Async operation status indicator for the lawyer sidebar.
 *
 * Shows the current state of async operations:
 * - Green: All queues empty, no pending operations
 * - Yellow (pulsing): Operations in progress
 * - Gray: Loading or no data
 *
 * Clicking refreshes the status.
 */
export function AsyncStatusIndicator({ isCollapsed }: AsyncStatusIndicatorProps) {
  const [status, setStatus] = useState<AsyncStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await coreAPI.getAsyncStatus();
      setStatus(data);
    } catch (err) {
      setError('Failed to fetch status');
      console.error('[AsyncStatusIndicator] Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchStatus();

    const interval = setInterval(fetchStatus, STATUS_POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchStatus]);

  const getIndicator = () => {
    if (loading && !status) {
      return {
        color: 'bg-gray-400',
        text: 'Loading...',
        description: 'Checking async operation status',
        pulse: true,
      };
    }

    if (error) {
      return {
        color: 'bg-gray-400',
        text: 'Unavailable',
        description: error,
        pulse: false,
      };
    }

    if (!status) {
      return {
        color: 'bg-gray-400',
        text: 'Unknown',
        description: 'Status unknown',
        pulse: false,
      };
    }

    // Calculate total pending operations
    const pendingSnapshots = status.pending_operations.snapshot_requests || 0;
    const pendingAmadeus = status.pending_operations.amadeus_commands || 0;
    const totalPending = pendingSnapshots + pendingAmadeus;

    // Calculate total queue length
    const queueTotal = Object.values(status.celery_queues)
      .filter((v): v is number => v !== null)
      .reduce((sum, v) => sum + v, 0);

    if (totalPending > 0 || queueTotal > 0) {
      const parts: string[] = [];
      if (pendingSnapshots > 0) parts.push(`${pendingSnapshots} snapshot(s)`);
      if (pendingAmadeus > 0) parts.push(`${pendingAmadeus} command(s)`);
      if (queueTotal > 0) parts.push(`${queueTotal} queued`);

      return {
        color: 'bg-yellow-500',
        text: `${totalPending + queueTotal} active`,
        description: parts.join(', '),
        pulse: true,
      };
    }

    return {
      color: 'bg-green-500',
      text: 'Idle',
      description: 'No pending operations',
      pulse: false,
    };
  };

  const { color, text, description, pulse } = getIndicator();

  const content = (
    <div
      onClick={fetchStatus}
      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-sidebar-border rounded transition-colors"
    >
      <div className="relative">
        <Activity className="h-4 w-4 text-sidebar-text/70" />
        <div
          className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${color} ${pulse ? 'animate-pulse' : ''}`}
        />
      </div>
      {!isCollapsed && (
        <span className="text-xs text-sidebar-foreground/70 truncate">
          {text}
        </span>
      )}
      {loading && (
        <RefreshCw className="h-3 w-3 text-sidebar-text/50 animate-spin ml-auto" />
      )}
    </div>
  );

  // Collapsed view: just the icon with tooltip
  if (isCollapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-center py-2">
              <div
                onClick={fetchStatus}
                className="relative cursor-pointer p-1 hover:bg-sidebar-border rounded transition-colors"
              >
                <Activity className="h-4 w-4 text-sidebar-text/70" />
                <div
                  className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${color} ${pulse ? 'animate-pulse' : ''}`}
                />
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p className="font-medium">Async Operations</p>
            <p className="text-xs text-muted-foreground">{description}</p>
            {status && (
              <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                <p>Queues: P:{status.celery_queues.playwright ?? '-'} D:{status.celery_queues.default ?? '-'} A:{status.celery_queues.amadeus ?? '-'}</p>
              </div>
            )}
            <p className="text-xs text-muted-foreground/60 mt-1">Click to refresh</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Expanded view
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right">
          <p className="font-medium">Async Operations</p>
          <p className="text-xs text-muted-foreground">{description}</p>
          {status && (
            <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
              <p>Playwright: {status.celery_queues.playwright ?? '-'}</p>
              <p>Default: {status.celery_queues.default ?? '-'}</p>
              <p>Amadeus: {status.celery_queues.amadeus ?? '-'}</p>
              <p>Monitoring: {status.celery_queues.monitoring ?? '-'}</p>
            </div>
          )}
          <p className="text-xs text-muted-foreground/60 mt-1">Click to refresh</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default AsyncStatusIndicator;
